import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Chip, TypographyProps, useTheme,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import _ from 'lodash';

import AutocompleteField from '../Autocomplete';

import {
  ArrayChip,
  ArrayDropdownItem,
  ArrayFieldContainer,
  ArrayInput,
  ArrayInputItem,
  ArrayItem,
  ArrayMenu,
  Display,
  DisplayContainer,
  Dropdown,
  DropdownIcon,
  DropdownItem,
  EditableArrayContainer,
  EditFieldContainer,
  FieldButton,
  FixedArrayContainer,
  FixedArrayItem,
  RootContainer,
  StyledDateField,
  StyledDateTimeField,
  StyledTextField,
} from './index.styles';

dayjs.extend(customParseFormat);

// TODO: Type interfaces of this component correctly as exclusive cases
export type FieldType = 'text' | 'long' | 'select' | 'array' | 'date' | 'time' | 'datetime' | 'number' | 'boolean' | 'autocomplete';

export type TypeOption = {
  label: string,
  value: any
}

type Value = string | number | boolean | (string | number)[]

type Data = {
  value: Value,
  label?: string
}

interface EditableProps {
  data?: Data,
  type?: FieldType,
  typeOptions?: TypeOption[],
  onChange: (data: Data) => void,
  onCommit: () => void,
  onCancel: () => void,
  onTab: () => void,
}

const DATETIME_FORMAT = 'DD/MM/YYYY hh:mm a';
const DATE_FORMAT = 'DD/MM/YYYY';
const TIME_FORMAT = 'hh:mm a';

const Editable = ({ data,
  type,
  typeOptions,
  onChange,
  onCommit,
  onCancel,
  onTab } : EditableProps) => {
  const theme = useTheme();

  const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    // Don't allow key-based commit for long text fields, as they could have
    // newline characters
    if (e.key === 'Enter' && type !== 'long') onCommit();
    else if (e.key === 'Escape') onCancel();
    // Note: This does not work on Chrome
    else if (e.key === 'Tab') onTab();
  };

  if (type === 'text' || type === 'number' || type === 'long') {
    return (
      <StyledTextField
        value={data?.value}
        onChange={(e) => {
          onChange({ value: e.target.value });
        }}
        multiline={type === 'long'}
        onKeyDown={onKeyDown}
        onBlur={onCommit}
        autoFocus
        // Focus to the end of the field
        onFocus={(e) => e.currentTarget.setSelectionRange(
          e.currentTarget.value.length,
          e.currentTarget.value.length,
        )}
      />
    );
  }

  if (type === 'select' || type === 'boolean') {
    return (
      <Dropdown
        value={data?.label || ''}
        onChange={(e) => onChange({
          label: e.target.value as string,
          value: typeOptions?.find((x) => x.label === e.target.value)?.value,
        })}
        IconComponent={DropdownIcon}
        MenuProps={{
          PaperProps: {
            sx: {
              mt: 1,
              borderRadius: 2,
            },
          },
          sx: {
            '& li.Mui-selected': {
              backgroundColor: `${theme.palette.primary.light}b3`,
              color: theme.palette.darkPrimary.main,
              fontWeight: '600',
            },
          },
        }}
        onKeyDown={onKeyDown}
        onBlur={onCommit}
        autoFocus
        defaultOpen
      >
        {typeOptions?.map((opt) => (
          <DropdownItem
            key={opt.label}
            value={opt.label}
          >
            {opt.label}
          </DropdownItem>
        ))}
      </Dropdown>
    );
  }

  if (type === 'autocomplete') {
    return (
      <AutocompleteField
        onChange={(e, v) => onChange({ value: v?.value ?? '', label: v?.label ?? '' })}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        options={typeOptions ?? []}
        onKeyDown={onKeyDown}
        fullWidth
        onBlur={onCommit}
        autoFocus
        autoHighlight
        openOnFocus
      />
    );
  }

  if (type === 'datetime' || type === 'date') {
    const sharedPickerProps = {
      onBlur: onCommit,
      onKeyDown,
      onChange: (newValue: any) => onChange({ value: dayjs(newValue).valueOf() }),
      value: dayjs(data?.value as number),
      autoFocus: true,
    };

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        {type === 'date' && (
          <StyledDateField
            format={DATE_FORMAT}
            {...sharedPickerProps}
          />
        )}
        {type === 'datetime' && (
          <StyledDateTimeField
            format={DATETIME_FORMAT}
            {...sharedPickerProps}
          />
        )}
      </LocalizationProvider>
    );
  }

  if (type === 'time') {
    const sharedPickerProps = {
      onBlur: onCommit,
      onKeyDown,
      onChange: (newValue: any) => onChange({
        value: dayjs(newValue).format('HHmm'),
      }),
      value: dayjs(data?.value as string, 'HHmm'),
      autoFocus: true,
    };

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <StyledDateTimeField
          format={TIME_FORMAT}
          {...sharedPickerProps}
        />
      </LocalizationProvider>
    );
  }

  if (type === 'array' && Array.isArray(data?.value)) {
    const [editArray, setEditArray]
     = useState<(string | number)[]>(data?.value as (string | number)[]);
    const [searchStr, setSearchStr] = useState('');
    const [searchRes, setSearchRes] = useState(typeOptions?.filter((x) =>
      editArray.includes(x.value)).slice(0, 5) || []);

    const inputRef = useRef<any>(null);
    // Use a randomly generated class name to detect when the focus has
    // left this group of components
    const className = useMemo(() => `Array-${Math.random().toString(36).slice(2)}`, []);

    const focusOnInput = () => inputRef.current?.focus();

    const clearSearchStr = () => setSearchStr('');
    const addToArray = (v: string | number) => {
      setEditArray([...editArray, v]);
    };
    const removeIndexFromArray = (index: number) => {
      const copyArray = [...editArray];
      copyArray.splice(index, 1);
      setEditArray(copyArray);
    };

    const onArrayBlur = () => {
      setTimeout(() => {
        if (!(document.activeElement === inputRef.current
        || _.intersection(document.activeElement?.classList, [
          'MuiChip-root',
          className,
        ]).length > 0)) {
          // If blurring off of a related component
          onCommit();
        }
      }, 10);
    };

    useEffect(() => {
      onChange({ value: editArray || [] });
    }, [editArray]);

    useEffect(() => {
      // Update search results when the search string or the list of input
      setSearchRes(typeOptions?.filter((x) =>
        !editArray.includes(x.value) &&
        (x.value.toString().toLowerCase().includes(searchStr.toLowerCase()) ||
        x.label.toString().toLowerCase().includes(searchStr.toLowerCase()))).slice(0, 5) || [],
      );
    }, [searchStr, editArray]);

    return (
      <ArrayFieldContainer
        className={className}
        onClick={focusOnInput}
        onBlur={onArrayBlur}
      >
        <EditableArrayContainer
          className={className}
          onClick={focusOnInput}
          onBlur={onArrayBlur}
        >
          {editArray.map((entry, index) => {
            const label = typeOptions?.find((opt) => opt.value === entry)?.label;
            return (
              <ArrayItem key={label} className={className}>
                <ArrayChip
                  className={className}
                  size="small"
                  label={label}
                  onKeyDown={onKeyDown}
                  onDelete={() => {
                    removeIndexFromArray(index);
                    // Focus on input after an entry is deleted to start listening for onBlur occur
                    focusOnInput();
                  }}
                // This component does not support, but can be modified to support
                // onClick events for each chip
                // onClick={}
                />
              </ArrayItem>
            );
          })}
          <ArrayInputItem key="input-field" className={className}>
            <ArrayInput
              className={className}
              inputRef={inputRef}
              autoFocus
              value={searchStr}
              onChange={(e) => setSearchStr(e.target.value)}
              onBlur={onArrayBlur}
              onKeyDown={(e) => {
                if (e.key === 'Backspace' && searchStr.length === 0) {
                  // If backspace is pressed while there is no input, then start
                  // deleting the array from the last value
                  removeIndexFromArray(-1);
                } else if (e.key === 'Enter' && searchRes.length > 0 && searchStr.length > 0) {
                  // If there is a search result  and enter is pressed, add the top
                  // element in the search
                  addToArray(searchRes[0].value);
                  clearSearchStr();
                } else {
                  onKeyDown(e);
                }
              }}
            />
          </ArrayInputItem>
        </EditableArrayContainer>
        <ArrayMenu
          className={className}
          open={document.activeElement === inputRef.current && searchRes.length > 0}
          anchorEl={inputRef.current}
          autoFocus={false}
          onFocus={focusOnInput}
          hideBackdrop
          disableAutoFocus
          disableEnforceFocus
        >
          {document.activeElement === inputRef.current && searchRes.map((opt) =>
            (
              <ArrayDropdownItem
                className={className}
                key={opt.label}
                value={opt.label}
                onClick={() => {
                  addToArray(opt.value);
                  clearSearchStr();
                }}
              >
                {opt.label}
              </ArrayDropdownItem>
            ))}
        </ArrayMenu>
      </ArrayFieldContainer>
    );
  }

  return null;
};

interface EditFieldProps {
  value?: Value,
  type?: FieldType,
  typeOptions?: TypeOption[],
  placeholder?: string,
  onSubmit?: (newValue: Value) => void,
  onTab?: () => void,
  onClick?: () => void,
  typographyProps?: TypographyProps,
  disabled?: boolean,
  editMode?: boolean,
  revertToOriginalValue?: boolean,
}

const EditField = ({
  value = '',
  type = 'text',
  typeOptions: typeOptionsProp,
  onSubmit = () => {},
  onTab = () => {},
  onClick = () => {},
  placeholder = '',
  typographyProps,
  disabled,
  editMode = false,
  revertToOriginalValue = false,
}: EditFieldProps) => {
  const theme = useTheme();

  // Memoize type options
  // If the type is boolean and typeOptions isn't defined, default to
  // True/False options
  const typeOptions = useMemo(() => ((type === 'boolean' && !typeOptionsProp) ? [
    { label: 'True', value: true },
    { label: 'False', value: false },
  ] : (typeOptionsProp || [])), [type, typeOptionsProp]);

  // Memoize original value
  const originalValue = useMemo(() => ({
    value,
    // For these types, the data should include the label
    label: (type === 'select' || type === 'autocomplete' || type === 'boolean') ? typeOptions?.find((x) => x.value === value)?.label : '',
  }), [value, typeOptions]);

  const [isEditing, setIsEditing] = useState<boolean>(editMode);
  const [committedData, setCommittedData] = useState<Data>(originalValue);
  const [data, setData] = useState<Data>(originalValue);

  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (editMode) setIsEditing(true);
  }, [editMode]);

  useEffect(() => {
    if (revertToOriginalValue) {
      setCommittedData(originalValue);
      setData(originalValue);
    }
  }, [revertToOriginalValue]);

  useEffect(() => {
    // If the original value passed in changes (ie. parent component re-renders), then reset
    setData(originalValue);
    setCommittedData(originalValue);
    setIsEditing(false);
  }, [originalValue]);

  const onCommitData = (d: Data) => {
    setCommittedData(d);
    // Commit edit text
    onSubmit(d.value);
    setIsEditing(false);
  };

  const onCancel = () => {
    // Reset to last committed value
    setCommittedData(committedData);
    setData(committedData);
    setIsEditing(false);
  };

  const onCommit = () => {
    if (type === 'number') {
      const numericalValue = parseFloat(data.value as string);
      if (Number.isNaN(numericalValue)) {
        // Revert value to last committed value if numerical value is invalid
        onCancel();
      } else {
        onCommitData({ value: numericalValue });
      }
    } else {
      onCommitData(data);
    }
  };

  const onChange = (newData: Data) => {
    setData(newData);
    if (type === 'select' || type === 'autocomplete') {
      // For 'select' type, the value selected should be locked in as soon as it is selected
      onCommitData(newData);
    }
  };

  const formatValue = (d: Data) => {
    if (type === 'datetime') {
      return dayjs(typeof d.value === 'number' ? d.value : 0).format(DATETIME_FORMAT);
    }
    if (type === 'date') {
      return dayjs(typeof d.value === 'number' ? d.value : 0).format(DATE_FORMAT);
    }
    // Time is displayed as an absolute time independent of timezone
    if (type === 'time') {
      return dayjs(typeof d.value === 'string' ? d.value : '0000', 'HHmm').format(TIME_FORMAT);
    }
    if (['select', 'autocomplete', 'boolean'].includes(type)) {
      return d.label;
    }
    return d.value;
  };

  return (
    <RootContainer>
      {(isEditing && !disabled) ? (
        <EditFieldContainer>
          <Editable
            data={data}
            type={type}
            typeOptions={typeOptions}
            onChange={onChange}
            onTab={onTab}
            onCommit={onCommit}
            onCancel={onCancel}
          />
          <FieldButton
            type="button"
            disableRipple
            onMouseDown={onCancel}
            ref={closeButtonRef}
          >
            <CloseIcon />
          </FieldButton>
        </EditFieldContainer>
      )
        : (
          <DisplayContainer
            onClick={() => {
              onClick();
              setIsEditing(true);
            }}
            disabled={disabled}
          >
            {type === 'array' ? (
              <FixedArrayContainer>
                {(data.value as (string | number)[]).map((entry) => {
                  const label = typeOptions?.find((opt) => opt.value === entry)?.label;
                  return (
                    <FixedArrayItem key={label}>
                      <Chip size="small" label={label} />
                    </FixedArrayItem>
                  );
                })}
              </FixedArrayContainer>
            ) : (
              <Display
                $multiLine={type === 'long'}
                color={committedData?.value !== '' ? 'inherit' : theme.palette.gray.main}
                {...typographyProps}
              >
                {
                  // Display 0 as 0, but display placeholder if string is empty
                  (committedData?.value !== '')
                    ? formatValue(committedData)
                    : placeholder
                }
              </Display>
            )}
          </DisplayContainer>
        )}
    </RootContainer>
  );
};

export default EditField;
